import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Newspaper } from "lucide-react";
import axios from "axios";
import { format } from "date-fns";
import { fr, enUS } from "date-fns/locale";
import { useLanguage } from "../i18n";
import { PageHero, Reveal } from "../components/Shared";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const News = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const { lang, t } = useLanguage();
  const tn = t.newsPage;
  const dateLocale = lang === "fr" ? fr : enUS;

  useEffect(() => {
    axios
      .get(`${API}/articles`)
      .then((res) => setArticles(res.data))
      .catch(() => setArticles([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div data-testid="news-page">
      <PageHero
        title={tn.title}
        subtitle={tn.subtitle}
        image="https://images.unsplash.com/photo-1534237710431-e2fc698436d0?w=1920&q=80"
      />

      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          {loading ? (
            <p className="text-center text-slate-500 font-light">...</p>
          ) : articles.length === 0 ? (
            <div className="text-center py-16">
              <Newspaper size={44} className="text-slate-300 mx-auto mb-5" />
              <p className="text-slate-500 font-light">{tn.empty}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="news-grid">
              {articles.map((a, i) => (
                <Reveal key={a.id} delay={(i % 3) * 110}>
                  <Link
                    to={`/actualites/${a.id}`}
                    onClick={() => window.scrollTo({ top: 0 })}
                    className="group bg-white rounded-lg overflow-hidden border border-slate-200 hover:border-transparent hover:shadow-xl transition-all duration-500 flex flex-col h-full"
                    data-testid={`news-card-${a.id}`}
                  >
                    {a.image && (
                      <div className="overflow-hidden h-52">
                        <img
                          src={a.image}
                          alt={a.title}
                          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                        />
                      </div>
                    )}
                    <div className="p-7 flex flex-col flex-1">
                      <div className="flex items-center gap-3 mb-3">
                        {a.category && (
                          <span className="text-[#2f6fd8] text-[11px] font-semibold uppercase tracking-[0.2em]">
                            {a.category}
                          </span>
                        )}
                        <span className="text-slate-400 text-xs">
                          {format(new Date(a.created_at), "d MMM yyyy", { locale: dateLocale })}
                        </span>
                      </div>
                      <h3 className="font-semibold text-[#1b2653] text-lg leading-snug mb-3 group-hover:text-[#2547a8] transition-colors duration-300">
                        {a.title}
                      </h3>
                      <p className="text-slate-500 text-sm font-light leading-relaxed flex-1">
                        {a.content.slice(0, 150)}...
                      </p>
                      <span className="mt-5 pt-4 border-t border-slate-100 text-[#2547a8] text-sm font-medium inline-flex items-center gap-1.5">
                        {tn.readMore}
                        <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform duration-300" />
                      </span>
                    </div>
                  </Link>
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default News;
